import { FileFacts } from './media-checks';
import { Sniffed, sniffFile } from './media-sniff';

/** What the browser can read from a chosen file with an `<img>` or a `<video>`: the sides, the length. Both are `null` when it cannot (HEIC, a codec Chrome lacks): the job checks then. */
export interface BrowserFacts {
  dims: { width: number; height: number } | null;
  durationSec: number | null;
}

const METADATA_TIMEOUT_MS = 8000;

function readImage(url: string): Promise<BrowserFacts> {
  return new Promise(resolve => {
    const img = new Image();
    const timer = setTimeout(() => finish(null), METADATA_TIMEOUT_MS);
    const finish = (dims: { width: number; height: number } | null): void => {
      clearTimeout(timer);
      img.onload = null;
      img.onerror = null;
      resolve({ dims, durationSec: null });
    };
    img.onload = () => finish(img.naturalWidth > 0 ? { width: img.naturalWidth, height: img.naturalHeight } : null);
    img.onerror = () => finish(null);
    img.src = url;
  });
}

function readVideo(url: string): Promise<BrowserFacts> {
  return new Promise(resolve => {
    const video = document.createElement('video');
    const timer = setTimeout(() => finish(null), METADATA_TIMEOUT_MS);
    const finish = (facts: BrowserFacts | null): void => {
      clearTimeout(timer);
      video.onloadedmetadata = null;
      video.onerror = null;
      video.removeAttribute('src');
      video.load();
      resolve(facts ?? { dims: null, durationSec: null });
    };
    video.preload = 'metadata';
    video.muted = true;
    video.onloadedmetadata = () =>
      finish({
        dims: video.videoWidth > 0 ? { width: video.videoWidth, height: video.videoHeight } : null,
        durationSec: Number.isFinite(video.duration) ? video.duration : null
      });
    video.onerror = () => finish(null);
    video.src = url;
  });
}

/** One frame of a video as a picture for the tile while the file goes up (`null` when the browser cannot decode it within a few seconds). */
export function captureVideoFrame(url: string): Promise<string | null> {
  return new Promise(resolve => {
    const video = document.createElement('video');
    const timer = setTimeout(() => finish(null), METADATA_TIMEOUT_MS);
    let settled = false;
    const finish = (value: string | null): void => {
      if (settled) {
        // the answer came after the time was up (the picture was still being encoded): nobody will use it, so its memory goes back
        if (value) {
          URL.revokeObjectURL(value);
        }
        return;
      }
      settled = true;
      clearTimeout(timer);
      video.onloadeddata = null;
      video.onseeked = null;
      video.onerror = null;
      video.removeAttribute('src');
      video.load();
      resolve(value);
    };
    const draw = (): void => {
      try {
        const scale = Math.min(1, 480 / Math.max(video.videoWidth, video.videoHeight, 1));
        const canvas = document.createElement('canvas');
        canvas.width = Math.max(1, Math.round(video.videoWidth * scale));
        canvas.height = Math.max(1, Math.round(video.videoHeight * scale));
        canvas.getContext('2d')?.drawImage(video, 0, 0, canvas.width, canvas.height);
        canvas.toBlob(blob => finish(blob ? URL.createObjectURL(blob) : null), 'image/jpeg', 0.7);
      } catch {
        finish(null);
      }
    };
    video.preload = 'auto';
    video.muted = true;
    video.playsInline = true;
    video.onloadeddata = () => {
      if (video.videoWidth <= 0) {
        finish(null);
        return;
      }
      video.onseeked = draw;
      video.currentTime = Math.min(1, (Number.isFinite(video.duration) ? video.duration : 1) / 2);
    };
    video.onerror = () => finish(null);
    video.src = url;
  });
}

export interface InspectedFile {
  facts: FileFacts;
  sniffed: Sniffed | null;
}

/** Looks at a chosen file: its first bytes, and, where the browser can, its sides and its length. Nothing is sent. */
export async function inspectFile(file: File): Promise<InspectedFile> {
  const sniffed = await sniffFile(file);
  let browser: BrowserFacts = { dims: null, durationSec: null };
  if (sniffed?.browserReadable && file.size > 0) {
    const url = URL.createObjectURL(file);
    try {
      browser = sniffed.type === 'VIDEO' ? await readVideo(url) : await readImage(url);
    } finally {
      URL.revokeObjectURL(url);
    }
  }
  return { sniffed, facts: { name: file.name, size: file.size, sniffed, dims: browser.dims, durationSec: browser.durationSec } };
}
